import { AlertCircle, Plus, RefreshCcw } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { ColocaTabs } from "@/shared/components/navigation/ColocaTabs";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { useBalancesHidden } from "@/shared/hooks/useBalancesHidden";
import { useUrlParamMirror } from "@/shared/hooks/useUrlParamMirror";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { usePayoutRecheck } from "../../hooks/usePayoutRecheck";
import { useProviderFloats } from "../../hooks/useProviderFloats";
import { useWalletsOverview } from "../../hooks/useWalletsOverview";
import { BalanceVisibilityToggle } from "../BalanceVisibilityToggle";
import { WalletMovementsTab } from "../movements/WalletMovementsTab";
import { PayoutRecheckNotice } from "./PayoutRecheckNotice";
import { ProviderFloatsPanel } from "./ProviderFloatsPanel";
import { WalletsCurrencySummary } from "./WalletsCurrencySummary";
import { WalletsOverviewFilters } from "./WalletsOverviewFilters";
import { type OverviewOrganization, WalletsOverviewTable } from "./WalletsOverviewTable";

type OverviewTab = "wallets" | "movements" | "providers";

function readTab(value: string | null): OverviewTab {
  return value === "providers" || value === "movements" ? value : "wallets";
}

export interface WalletsOverviewProps {
  onOpenOrganization: (organization: OverviewOrganization) => void;
  onActivateForOrganization: () => void;
  /** Bumped by the host when it comes back from a view that may have moved money. */
  reloadSignal?: number;
}

/**
 * INTERNAL staff home of `/app/wallets`: how much Coloca must back in each currency across every client's
 * payment wallets, and the wallets themselves, filtered and ranked by balance.
 */
export function WalletsOverview({
  onOpenOrganization,
  onActivateForOrganization,
  reloadSignal = 0,
}: WalletsOverviewProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const { hidden: masked } = useBalancesHidden();
  const { can } = useAuth();
  const overview = useWalletsOverview(reloadSignal);
  const { filters, setFilters, summary } = overview;

  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<OverviewTab>(() => readTab(searchParams.get("tab")));
  useUrlParamMirror("tab", tab === "wallets" ? null : tab);
  // Each tab reads only once opened.
  const [movementsOpened, setMovementsOpened] = useState(tab === "movements");
  const [movementsReload, setMovementsReload] = useState(0);
  // Read only once the tab is opened: every read can reach the providers.
  const [providersOpened, setProvidersOpened] = useState(tab === "providers");
  const [providerReload, setProviderReload] = useState(0);
  const floats = useProviderFloats(providersOpened, reloadSignal + providerReload);
  // Asked once per visit: a payout stuck "in progress" keeps a client's balance blocked until its provider is
  // read again. When the run moves any payout, the page reads its balances again.
  const recheck = usePayoutRecheck(true, () => {
    overview.refresh();
    if (providersOpened) setProviderReload((value) => value + 1);
  });

  const heldByCurrency = useMemo(
    () => new Map((summary?.currencies ?? []).map((row) => [row.currency, row.held])),
    [summary],
  );
  const currencies = useMemo(() => (summary?.currencies ?? []).map((row) => row.currency).sort(), [summary]);

  if (!overview.hasAccess) {
    return <NoAccessView />;
  }

  const updatedAt = summary
    ? formatDateTime(summary.generated_at, { locale: activeLanguage, includeTime: true })
    : null;
  const isRefreshing = overview.isLoadingRows || overview.isLoadingSummary || floats.isLoading;

  const walletsPanel = (
    <>
      <section className="space-y-2">
        <WalletsCurrencySummary
          summary={summary}
          isLoading={overview.isLoadingSummary}
          error={overview.summaryError}
          selectedCurrency={filters.currency}
          onSelectCurrency={(currency) => setFilters({ currency })}
          masked={masked}
          hideEmpty={filters.withBalance}
        />
        <p className="text-xs text-muted-foreground">{t("summary_footnote")}</p>
      </section>

      <section className="space-y-4">
        <WalletsOverviewFilters
          filters={filters}
          onChange={setFilters}
          onClear={overview.resetFilters}
          hasActiveFilters={overview.hasActiveFilters}
          currencies={currencies}
        />

        {overview.rowsError ? (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={t("rows_error")}
            actions={
              <ColocaButton variant="secondary" size="sm" onClick={overview.refresh}>
                {t("retry")}
              </ColocaButton>
            }
          />
        ) : (
          <WalletsOverviewTable
            rows={overview.rows}
            total={overview.total}
            page={overview.page}
            onPageChange={overview.setPage}
            isLoading={overview.isLoadingRows}
            heldByCurrency={heldByCurrency}
            masked={masked}
            hasActiveFilters={overview.hasActiveFilters}
            onClearFilters={overview.resetFilters}
            hiddenWithoutBalance={overview.hiddenWithoutBalance}
            onShowWithoutBalance={() => setFilters({ withBalance: false })}
            onOpenOrganization={onOpenOrganization}
          />
        )}
      </section>
    </>
  );

  return (
    <div className="space-y-6">
      <ColocaPageHeader
        title={t("overview_title")}
        description={t("overview_subtitle")}
        actions={
          <>
            <BalanceVisibilityToggle />
            <label
              className="flex h-8 cursor-pointer items-center gap-2 rounded-md border border-border px-2.5 text-xs text-foreground"
              title={t("overview_include_coloca_hint")}
            >
              <ColocaSwitch
                checked={filters.includeColoca}
                onCheckedChange={(includeColoca) => setFilters({ includeColoca })}
                size="sm"
              />
              {t("overview_include_coloca")}
            </label>
            <ColocaButton
              variant="secondary"
              size="sm"
              icon={RefreshCcw}
              onClick={() => {
                overview.refresh();
                recheck.start();
                setMovementsReload((value) => value + 1);
                if (providersOpened) setProviderReload((value) => value + 1);
              }}
              disabled={isRefreshing}
            >
              {t("overview_refresh")}
            </ColocaButton>
            <ColocaButton variant="primary" size="sm" icon={Plus} onClick={onActivateForOrganization}>
              {t("overview_activate_for_org")}
            </ColocaButton>
          </>
        }
      >
        {updatedAt && (
          <p className="mt-1 text-xs text-muted-foreground">{t("overview_updated", { time: updatedAt })}</p>
        )}
        <PayoutRecheckNotice run={recheck.run} />
      </ColocaPageHeader>

      <ColocaTabs
        value={tab}
        // Three full names do not fit a phone in one row: two per row below `sm` (design-system rule 12).
        listClassName="h-auto! w-full flex-wrap gap-1 sm:h-9! sm:flex-nowrap sm:gap-0"
        triggerClassName="min-h-11 grow basis-[45%] sm:min-h-0 sm:basis-0"
        onValueChange={(value) => {
          const next = readTab(value);
          setTab(next);
          if (next === "providers") setProvidersOpened(true);
          if (next === "movements") setMovementsOpened(true);
        }}
        tabs={[
          {
            value: "wallets",
            label: t("tab_wallets"),
            content: <div className="space-y-6 pt-4">{walletsPanel}</div>,
          },
          {
            value: "movements",
            label: t("tab_movements"),
            content: (
              <div className="pt-4">
                <WalletMovementsTab
                  enabled={movementsOpened}
                  includeColoca={filters.includeColoca}
                  masked={masked}
                  currencies={currencies}
                  reloadSignal={reloadSignal + movementsReload}
                />
              </div>
            ),
          },
          {
            value: "providers",
            label: t("tab_providers"),
            content: (
              <div className="pt-4">
                <ProviderFloatsPanel
                  data={floats.data}
                  isLoading={floats.isLoading}
                  error={floats.error}
                  hasAccess={floats.hasAccess}
                  currencies={summary?.currencies ?? []}
                  masked={masked}
                  onReadAgain={can("payments:write") ? floats.readAgain : undefined}
                  refreshDenied={floats.refreshDenied}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
